// Hardcoded sample diagnostics shown by "See an example" — one per language,
// same structure as what the LLM produces so the preview is representative.
// Current example: Tomato Market & Logistics Matchmaking Assistant
// (Kirinyaga County, Kenya). Pick with getExampleDiagnostic(lang).

const EN = `# AI Diagnostic and Scoping
**Use-case name:** Tomato Market & Logistics Matchmaking Assistant

Tags : #agriculture · #tomatoes · #marketAccess · #smallholderFarmers · #Kenya · #logistics

## Summary
- **Organisation:** Individual tomato producer (Kirinyaga County), potentially cooperatives/NGOs
- **Sector:** Agriculture – horticulture (tomato value chain)
- **Functions concerned:** Production planning, sales/marketing, logistics, finance/cash-flow
- **Target users:** Tomato farmers, aggregators, traders, wholesalers, transporters, NGOs/cooperatives
- **Priority level:** High
- **Complexity level:** Medium
- **Time to first value:** 3 to 6 months
- **Recommendation:** Launch a mobile-first MVP connecting harvest forecasts with buyers and logistics, then scale.

## 1. Identified business challenge
Tomatoes in Kirinyaga County are highly perishable, and producers often reach harvest time without reliable, timely information on buyer demand, farm-gate prices, aggregation opportunities or quality requirements. This leads to uncertainty, waste and weak bargaining power for farmers.

Consequences:
- Frequent post-harvest losses due to unsold or late-sold tomatoes
- Selling at suboptimal prices because of last-minute, distressed sales
- Mismatch between farmer production (variety, grade, timing) and buyer needs
- Inefficient logistics and higher transport costs due to poor coordination
- Cash-flow uncertainty for farmers (not knowing when they will be paid)
- Limited transparency and trust between farmers, traders and aggregators
- Difficulty planning future planting and investment decisions

**Key challenge:** Align perishable tomato supply with real-time buyer demand, prices and logistics in a simple, trusted way for all value-chain actors.

**Primary objective:** Enable farmers to know, ahead of harvest, who will buy their tomatoes, at what net price, how they will be transported, and when payment will be received.

**Secondary objectives:**
- Reduce post-harvest losses and waste for tomato producers
- Improve farm-gate prices and negotiation power for farmers
- Increase transparency on quality requirements and grading standards
- Coordinate aggregation and transport to reduce logistics costs
- Provide better data for cooperatives/NGOs to support market access
- Build a digital history of harvests, prices and buyers for planning

## 2. Recommended AI use case
**"Smart Tomato Market & Logistics Matcher"**

A mobile-first assistant where farmers share expected harvest details and, in return, receive tailored recommendations on buyers, net prices, aggregation points, logistics options and expected payment timelines. The tool also helps buyers and aggregators see upcoming supply and plan purchases and transport.

**Main features:**
- Simple farmer interface (mobile app, WhatsApp bot or USSD) to input expected harvest: variety, number of crates, grade, ripeness, likely harvest date, location
- Matching engine that suggests potential buyers (traders, aggregators, wholesalers) based on demand, location, quality and timing
- Indicative net price ranges at farm-gate, including estimated transport and commission costs
- Recommendations of aggregation points and transport options (e.g. shared trucks, collection routes)
- Clear indication of expected payment terms and timelines per buyer type
- Notifications and alerts when new demand appears or prices change in nearby markets
- Basic quality guidance: what grade/quality buyers require, with examples and tips
- Simple dashboards for cooperatives/NGOs to monitor supply, demand and coordination

## 3. Recommended level of automation
**Phase 1 — Decision support**: Provide farmers and buyers with recommendations and price/demand insights, leaving final decisions and negotiations to humans.

**Phase 2 — Semi-automatic notification/action**: Automatically notify matched buyers and aggregators of upcoming harvests and allow them to confirm interest, with farmers approving final deals.

**Phase 3 — Controlled automation**: Enable semi-automated booking of transport and purchase agreements under predefined rules, with human oversight and easy override.

## 4. Required data
**Internal data (from users and partners):**
- Farmer harvest forecasts: variety, estimated crates, grade/quality, ripeness, harvest date
- Farm location: GPS pin, village, nearest aggregation point
- Photos of crops and harvested tomatoes (for quality/grade assessment)
- Voice notes describing crop condition, issues, or special requirements
- Historical sales data: dates, volumes, prices, buyers, payment delays (where available)
- Buyer profiles: type (trader, aggregator, wholesaler), preferred varieties, quality standards, typical volumes
- Buyer demand data: planned purchase dates, quantities, target prices, locations
- Logistics data: available trucks, routes, capacity, typical costs, schedules
- Cooperative/NGO records on member farmers, aggregation centers and past coordination activities

**External data:**
- Market price feeds from local markets (e.g. county market reports, NGO data)
- Public agricultural extension information on tomato varieties, grading and post-harvest handling
- Weather forecasts affecting harvest timing and road conditions
- Road and distance data (e.g. from mapping services) for transport cost estimation
- Mobile network coverage information to adapt communication channels (WhatsApp, SMS, USSD)

**Data watchpoints:**
- Data quality and reliability of farmer forecasts and buyer commitments
- Standardization of grades/quality definitions across different actors
- Privacy and consent for sharing farmer and buyer contact/location data
- Fair access and avoidance of data misuse by powerful intermediaries
- Compliance with data protection regulations (including GDPR if any EU partners are involved)

## 5. Technical orientations
**Recommended AI components:**
- **General-purpose LLM** (e.g. GPT-4o, Claude Sonnet, Llama-3) for conversational interfaces (WhatsApp bot, mobile chat) and explanation of prices, quality requirements and logistics options in simple language
- **Classic ML** (e.g. XGBoost or similar) for price range prediction and demand forecasting based on historical sales, seasonality and location
- **Embeddings + semantic search** to match farmer harvest descriptions with buyer demand profiles and quality requirements
- **Computer vision** (e.g. lightweight CNN or ViT + mobile-friendly OCR) to assess tomato quality/grade from photos and flag issues (overripe, damaged)
- **NLP classification** to interpret voice notes and free-text descriptions into structured attributes (e.g. "slightly overripe", "cracked skin")
- **Multi-agent / orchestration** (e.g. LangGraph) to coordinate matching, price estimation, logistics suggestions and notifications in a robust workflow

**Target architecture:**
- Hybrid processing: near real-time for matching and notifications; batch for daily price/demand updates and model retraining
- Application style: modular microservices or serverless functions to keep costs low and scale with usage
- Integration mode: APIs for mobile app/WhatsApp bot, webhooks for notifications to buyers and transporters, possible USSD/SMS gateway for low-end phones
- Human-in-the-loop pattern: farmers and buyers must confirm matches and deals; fallback to manual coordination via cooperatives/NGOs; A/B testing of recommendation strategies

**Key integrations:**
- WhatsApp Business API and/or SMS/USSD gateway for farmer and trader communication
- Simple mobile app or web portal for cooperatives/NGOs and larger buyers
- Mapping services (e.g. Google Maps, OpenStreetMap) for distance and route estimation
- Any existing price information services or market bulletin systems used locally
- Payment solutions (mobile money platforms like M-Pesa) for tracking payment status and timelines

**Build vs Buy:** Hybrid — leverage existing messaging and payment platforms plus off-the-shelf AI models, while building a custom matching and market-intelligence layer tailored to the Kirinyaga tomato value chain.

**MLOps & governance:**
- Monitor recommendation quality (price accuracy, match success rate, user satisfaction)
- Regular retraining of price and demand models with new transaction data
- Maintain audit logs of recommendations and final deals for transparency
- Manage bias and fairness (ensure small farmers are not disadvantaged; comply with relevant data protection rules)
- Versioning of prompts, models and datasets to track changes and performance over time

**Suggested technical stack:**
- Cloud / runtime: Lightweight cloud (e.g. AWS, Azure, or GCP) or regional provider with good African connectivity
- AI models: GPT-4o or Claude Sonnet for conversational layer; open-source Llama-3 for cost-sensitive deployments; small CNN/ViT for image grading; XGBoost for price/demand prediction
- Data layer: Relational database (e.g. PostgreSQL) for transactions; object storage for images and voice notes; vector database (e.g. Pinecone, Weaviate, or pgvector) for semantic matching
- Orchestration / pipeline: LangGraph or similar for multi-step workflows; Airflow or serverless scheduled jobs for batch updates and retraining
- Observability: Application monitoring (e.g. Prometheus + Grafana), logging (e.g. ELK stack), and custom dashboards for model performance and user metrics

## 6. Use-case scoring

| Criterion | Score | Analysis |
| --- | --- | --- |
| Business impact | 9/10 | Reduces waste, improves prices, stabilizes income |
| Data feasibility | 7/10 | Farmer and buyer data available but needs structuring |
| Technical feasibility | 8/10 | Uses mature AI and messaging technologies |
| Business adoption | 8/10 | High need; WhatsApp familiarity supports uptake |
| Regulatory risk | 6/10 | Moderate; mainly data privacy and fairness concerns |
| ROI potential | 9/10 | Strong gains from reduced losses and better prices |

**Estimated overall score : 78/100**

**Verdict:** Strong, high-impact use case suitable for a focused MVP and progressive scaling across the tomato value chain.

## 7. Success indicators
**Business indicators:**
- Reduction in post-harvest losses (% decrease in unsold or spoiled tomatoes)
- Increase in average farm-gate price per crate (percentage improvement)
- Share of harvest volumes sold through the platform vs. traditional channels
- Average time from harvest to sale confirmation and payment
- Number of active farmers, buyers and transporters using the tool
- Frequency of successful matches (harvests matched to buyers with completed transactions)

**AI indicators:**
- Accuracy of price range predictions vs. actual transaction prices
- Match success rate (recommended buyer-logistics combinations leading to completed deals)
- Quality of image-based grading (agreement with human graders)
- User satisfaction scores from farmers and buyers (simple rating after transactions)
- Model drift metrics (changes in price/demand patterns over time)
- Latency and reliability of recommendations and notifications

## 8. Conclusion
Tomato producers in Kirinyaga County face a critical challenge: aligning highly perishable harvests with real-time buyer demand, fair prices and reliable logistics. A mobile-first, AI-supported "Smart Tomato Market & Logistics Matcher" can directly address this by turning simple farmer inputs into actionable recommendations on buyers, prices, aggregation points and payment expectations.

By starting with a lean MVP focused on WhatsApp/SMS interfaces, basic matching and price guidance, and involving cooperatives/NGOs as coordinators, the solution can quickly demonstrate value and build trust among all actors in the value chain. Over time, richer features like image-based grading, demand forecasting and semi-automated logistics booking can be added.

**Recommendation:** Launch a targeted MVP in Kirinyaga with a small group of farmers, buyers and an NGO/cooperative, then iterate and scale based on real-world feedback.`;

const FR = `# Diagnostic et cadrage IA
**Nom du cas d'usage :** Assistant de mise en relation marché & logistique pour la tomate

Tags : #agriculture · #tomates · #accèsmarché · #petitsproducteurs · #Kenya · #logistique

## Synthèse
- **Organisation :** Producteur individuel de tomates (comté de Kirinyaga), potentiellement coopératives/ONG
- **Secteur :** Agriculture – horticulture (filière tomate)
- **Fonctions concernées :** Planification de production, ventes/marketing, logistique, finance/trésorerie
- **Utilisateurs cibles :** Producteurs de tomates, agrégateurs, commerçants, grossistes, transporteurs, ONG/coopératives
- **Niveau de priorité :** Élevé
- **Niveau de complexité :** Moyen
- **Horizon de première valeur :** 3 à 6 mois
- **Recommandation :** Lancer un MVP mobile-first reliant les prévisions de récolte aux acheteurs et à la logistique, puis passer à l'échelle.

## 1. Enjeu métier identifié
Les tomates du comté de Kirinyaga sont hautement périssables, et les producteurs arrivent souvent à la récolte sans information fiable et à jour sur la demande des acheteurs, les prix bord-champ, les opportunités d'agrégation ou les exigences de qualité. Cela entraîne incertitude, gaspillage et faible pouvoir de négociation pour les agriculteurs.

Conséquences :
- Pertes post-récolte fréquentes dues aux tomates invendues ou vendues tardivement
- Ventes à des prix sous-optimaux à cause de ventes précipitées de dernière minute
- Décalage entre la production (variété, calibre, calendrier) et les besoins des acheteurs
- Logistique inefficace et coûts de transport plus élevés faute de coordination
- Incertitude de trésorerie pour les agriculteurs (date de paiement inconnue)
- Transparence et confiance limitées entre agriculteurs, commerçants et agrégateurs
- Difficulté à planifier les prochaines plantations et décisions d'investissement

**Défi clé :** Aligner l'offre périssable de tomates avec la demande en temps réel, les prix et la logistique, de façon simple et fiable pour tous les acteurs de la filière.

**Objectif principal :** Permettre aux agriculteurs de savoir, avant la récolte, qui achètera leurs tomates, à quel prix net, comment elles seront transportées et quand le paiement sera reçu.

**Objectifs secondaires :**
- Réduire les pertes post-récolte et le gaspillage
- Améliorer les prix bord-champ et le pouvoir de négociation des agriculteurs
- Accroître la transparence sur les exigences de qualité et les standards de calibrage
- Coordonner l'agrégation et le transport pour réduire les coûts logistiques
- Fournir de meilleures données aux coopératives/ONG pour soutenir l'accès au marché
- Constituer un historique numérique des récoltes, prix et acheteurs pour la planification

## 2. Cas d'usage IA recommandé
**« Smart Tomato Market & Logistics Matcher »**

Un assistant mobile-first où les agriculteurs partagent les détails de leur récolte attendue et reçoivent en retour des recommandations personnalisées sur les acheteurs, les prix nets, les points d'agrégation, les options logistiques et les délais de paiement attendus. L'outil aide aussi les acheteurs et agrégateurs à visualiser l'offre à venir et à planifier achats et transport.

**Fonctionnalités principales :**
- Interface agriculteur simple (appli mobile, bot WhatsApp ou USSD) pour saisir la récolte attendue : variété, nombre de cageots, calibre, maturité, date probable, localisation
- Moteur de matching suggérant des acheteurs potentiels (commerçants, agrégateurs, grossistes) selon la demande, la localisation, la qualité et le calendrier
- Fourchettes de prix nets indicatives bord-champ, incluant coûts estimés de transport et commissions
- Recommandations de points d'agrégation et d'options de transport (camions partagés, tournées de collecte)
- Indication claire des conditions et délais de paiement par type d'acheteur
- Notifications et alertes quand une nouvelle demande apparaît ou que les prix changent sur les marchés proches
- Conseils qualité de base : calibre/qualité exigés par les acheteurs, avec exemples et astuces
- Tableaux de bord simples pour les coopératives/ONG (suivi offre, demande, coordination)

## 3. Niveau d'automatisation recommandé
**Phase 1 — Aide à la décision** : fournir aux agriculteurs et acheteurs des recommandations et des informations prix/demande, en laissant décisions finales et négociations aux humains.

**Phase 2 — Notification / action semi-automatique** : notifier automatiquement les acheteurs et agrégateurs correspondants des récoltes à venir et leur permettre de confirmer leur intérêt, l'agriculteur validant l'accord final.

**Phase 3 — Automatisation contrôlée** : permettre la réservation semi-automatisée du transport et des accords d'achat selon des règles prédéfinies, avec supervision humaine et possibilité de reprise en main.

## 4. Données nécessaires
**Données internes (utilisateurs et partenaires) :**
- Prévisions de récolte : variété, cageots estimés, calibre/qualité, maturité, date de récolte
- Localisation de la ferme : point GPS, village, point d'agrégation le plus proche
- Photos des cultures et des tomates récoltées (évaluation qualité/calibre)
- Notes vocales décrivant l'état des cultures, problèmes ou exigences particulières
- Historique des ventes : dates, volumes, prix, acheteurs, délais de paiement (si disponibles)
- Profils acheteurs : type (commerçant, agrégateur, grossiste), variétés préférées, standards qualité, volumes types
- Données de demande : dates d'achat prévues, quantités, prix cibles, localisations
- Données logistiques : camions disponibles, itinéraires, capacités, coûts types, horaires
- Registres coopératives/ONG : membres, centres d'agrégation, coordinations passées

**Données externes :**
- Flux de prix des marchés locaux (rapports de marché du comté, données ONG)
- Informations publiques de vulgarisation agricole (variétés, calibrage, post-récolte)
- Prévisions météo affectant le calendrier de récolte et l'état des routes
- Données routières et de distance (services cartographiques) pour estimer les coûts de transport
- Couverture réseau mobile pour adapter les canaux (WhatsApp, SMS, USSD)

**Points de vigilance data :**
- Qualité et fiabilité des prévisions agriculteurs et des engagements acheteurs
- Standardisation des définitions de calibre/qualité entre acteurs
- Confidentialité et consentement pour le partage des contacts/localisations
- Accès équitable et prévention des abus de données par des intermédiaires puissants
- Conformité aux réglementations de protection des données (RGPD si partenaires UE)

## 5. Orientations techniques
**Briques IA recommandées :**
- **LLM généraliste** (GPT-4o, Claude Sonnet, Llama-3) pour les interfaces conversationnelles (bot WhatsApp, chat mobile) et l'explication des prix, exigences qualité et options logistiques en langage simple
- **ML classique** (XGBoost ou similaire) pour la prédiction de fourchettes de prix et la prévision de demande (historique, saisonnalité, localisation)
- **Embeddings + recherche sémantique** pour apparier descriptions de récoltes et profils de demande/exigences qualité
- **Vision par ordinateur** (CNN léger ou ViT + OCR mobile) pour évaluer la qualité/le calibre sur photos et signaler les problèmes (surmaturité, dégâts)
- **Classification NLP** pour transformer notes vocales et textes libres en attributs structurés (« légèrement trop mûres », « peau fendue »)
- **Multi-agents / orchestration** (LangGraph) pour coordonner matching, estimation de prix, suggestions logistiques et notifications dans un workflow robuste

**Architecture cible :**
- Traitement hybride : quasi temps réel pour matching et notifications ; batch pour les mises à jour quotidiennes prix/demande et le réentraînement
- Style applicatif : microservices modulaires ou fonctions serverless pour maîtriser les coûts et scaler avec l'usage
- Intégration : API pour l'appli/bot WhatsApp, webhooks pour notifier acheteurs et transporteurs, passerelle USSD/SMS pour téléphones basiques
- Human-in-the-loop : agriculteurs et acheteurs confirment les accords ; repli sur coordination manuelle via coopératives/ONG ; A/B testing des stratégies de recommandation

**Intégrations clés :**
- API WhatsApp Business et/ou passerelle SMS/USSD
- Appli mobile ou portail web simple pour coopératives/ONG et gros acheteurs
- Services cartographiques (Google Maps, OpenStreetMap) pour distances et itinéraires
- Services d'information prix ou bulletins de marché existants localement
- Solutions de paiement (mobile money type M-Pesa) pour suivre statuts et délais de paiement

**Build vs Buy :** Hybride — s'appuyer sur les plateformes de messagerie et de paiement existantes et les modèles IA du marché, tout en construisant la couche de matching et d'intelligence marché propre à la filière tomate de Kirinyaga.

**MLOps & gouvernance :**
- Suivi de la qualité des recommandations (précision prix, taux de matching réussi, satisfaction)
- Réentraînement régulier des modèles prix/demande avec les nouvelles transactions
- Journaux d'audit des recommandations et accords finaux pour la transparence
- Gestion des biais et de l'équité (ne pas désavantager les petits producteurs ; conformité protection des données)
- Versioning des prompts, modèles et jeux de données

**Stack technique pressentie :**
- Cloud / runtime : cloud léger (AWS, Azure, GCP) ou fournisseur régional bien connecté à l'Afrique
- Modèles IA : GPT-4o ou Claude Sonnet pour la couche conversationnelle ; Llama-3 open source pour les déploiements sensibles aux coûts ; petit CNN/ViT pour le calibrage image ; XGBoost pour prix/demande
- Couche données : base relationnelle (PostgreSQL) pour les transactions ; stockage objet pour images et notes vocales ; base vectorielle (Pinecone, Weaviate ou pgvector) pour le matching sémantique
- Orchestration : LangGraph pour les workflows multi-étapes ; Airflow ou jobs serverless planifiés pour le batch et le réentraînement
- Observabilité : monitoring applicatif (Prometheus + Grafana), logs (ELK), tableaux de bord dédiés performance modèles et usage

## 6. Scoring du cas d'usage

| Critère | Score | Analyse |
| --- | --- | --- |
| Impact métier | 9/10 | Réduit le gaspillage, améliore les prix, stabilise les revenus |
| Faisabilité data | 7/10 | Données agriculteurs/acheteurs disponibles mais à structurer |
| Faisabilité technique | 8/10 | Technologies IA et messagerie matures |
| Adoption métier | 8/10 | Fort besoin ; la familiarité WhatsApp facilite l'adoption |
| Risque réglementaire | 6/10 | Modéré ; surtout confidentialité des données et équité |
| Potentiel ROI | 9/10 | Gains importants via pertes réduites et meilleurs prix |

**Score global estimé : 78/100**

**Verdict :** Cas d'usage solide à fort impact, adapté à un MVP ciblé puis à une montée en charge progressive sur la filière tomate.

## 7. Indicateurs de succès
**Indicateurs métier :**
- Réduction des pertes post-récolte (% de tomates invendues ou gâtées en moins)
- Augmentation du prix moyen bord-champ par cageot (% d'amélioration)
- Part des volumes vendus via la plateforme vs circuits traditionnels
- Délai moyen entre récolte, confirmation de vente et paiement
- Nombre d'agriculteurs, acheteurs et transporteurs actifs
- Fréquence des matchings réussis (récoltes appariées avec transactions conclues)

**Indicateurs IA :**
- Précision des fourchettes de prix prédites vs prix réels
- Taux de matching réussi (combinaisons acheteur-logistique menant à des accords conclus)
- Qualité du calibrage par image (accord avec des évaluateurs humains)
- Scores de satisfaction agriculteurs/acheteurs (note simple après transaction)
- Métriques de dérive des modèles (évolution des schémas prix/demande)
- Latence et fiabilité des recommandations et notifications

## 8. Conclusion
Les producteurs de tomates du comté de Kirinyaga font face à un défi critique : aligner des récoltes hautement périssables avec la demande en temps réel, des prix équitables et une logistique fiable. Un « Smart Tomato Market & Logistics Matcher » mobile-first, appuyé par l'IA, y répond directement en transformant des saisies simples en recommandations actionnables sur les acheteurs, les prix, les points d'agrégation et les délais de paiement.

En commençant par un MVP léger centré sur WhatsApp/SMS, un matching de base et des indications de prix, avec les coopératives/ONG comme coordinateurs, la solution peut démontrer rapidement sa valeur et instaurer la confiance dans la filière. Avec le temps, des fonctions plus riches (calibrage par image, prévision de demande, réservation logistique semi-automatisée) pourront s'ajouter.

**Recommandation :** Lancer un MVP ciblé à Kirinyaga avec un petit groupe d'agriculteurs, d'acheteurs et une ONG/coopérative, puis itérer et passer à l'échelle sur la base des retours terrain.`;

const ES = `# Diagnóstico y encuadre IA
**Nombre del caso de uso:** Asistente de conexión mercado y logística para el tomate

Tags : #agricultura · #tomates · #accesoalmercado · #pequeñosproductores · #Kenia · #logística

## Síntesis
- **Organización:** Productor individual de tomate (condado de Kirinyaga), potencialmente cooperativas/ONG
- **Sector:** Agricultura – horticultura (cadena de valor del tomate)
- **Funciones implicadas:** Planificación de producción, ventas/marketing, logística, finanzas/tesorería
- **Usuarios objetivo:** Productores de tomate, agregadores, comerciantes, mayoristas, transportistas, ONG/cooperativas
- **Nivel de prioridad:** Alto
- **Nivel de complejidad:** Medio
- **Horizonte de primer valor:** 3 a 6 meses
- **Recomendación:** Lanzar un MVP mobile-first que conecte previsiones de cosecha con compradores y logística, y luego escalar.

## 1. Reto de negocio identificado
Los tomates del condado de Kirinyaga son altamente perecederos, y los productores llegan a menudo a la cosecha sin información fiable y oportuna sobre la demanda de los compradores, los precios en finca, las oportunidades de agregación o los requisitos de calidad. Esto genera incertidumbre, desperdicio y un débil poder de negociación para los agricultores.

Consecuencias:
- Pérdidas postcosecha frecuentes por tomates no vendidos o vendidos tarde
- Ventas a precios subóptimos por ventas apuradas de último minuto
- Desajuste entre la producción (variedad, calibre, calendario) y las necesidades de los compradores
- Logística ineficiente y mayores costes de transporte por falta de coordinación
- Incertidumbre de tesorería para los agricultores (fecha de pago desconocida)
- Transparencia y confianza limitadas entre agricultores, comerciantes y agregadores
- Dificultad para planificar las próximas siembras y decisiones de inversión

**Reto clave:** Alinear la oferta perecedera de tomate con la demanda en tiempo real, los precios y la logística, de forma simple y fiable para todos los actores de la cadena.

**Objetivo principal:** Permitir a los agricultores saber, antes de la cosecha, quién comprará sus tomates, a qué precio neto, cómo se transportarán y cuándo recibirán el pago.

**Objetivos secundarios:**
- Reducir las pérdidas postcosecha y el desperdicio
- Mejorar los precios en finca y el poder de negociación de los agricultores
- Aumentar la transparencia sobre los requisitos de calidad y los estándares de calibrado
- Coordinar la agregación y el transporte para reducir los costes logísticos
- Aportar mejores datos a cooperativas/ONG para apoyar el acceso al mercado
- Construir un historial digital de cosechas, precios y compradores para la planificación

## 2. Caso de uso IA recomendado
**«Smart Tomato Market & Logistics Matcher»**

Un asistente mobile-first donde los agricultores comparten los detalles de su cosecha prevista y reciben recomendaciones personalizadas sobre compradores, precios netos, puntos de agregación, opciones logísticas y plazos de pago esperados. La herramienta también ayuda a compradores y agregadores a visualizar la oferta futura y planificar compras y transporte.

**Funcionalidades principales:**
- Interfaz simple para el agricultor (app móvil, bot de WhatsApp o USSD) para registrar la cosecha prevista: variedad, número de cajas, calibre, madurez, fecha probable, ubicación
- Motor de matching que sugiere compradores potenciales (comerciantes, agregadores, mayoristas) según demanda, ubicación, calidad y calendario
- Rangos de precios netos indicativos en finca, incluyendo costes estimados de transporte y comisiones
- Recomendaciones de puntos de agregación y opciones de transporte (camiones compartidos, rutas de recogida)
- Indicación clara de las condiciones y plazos de pago por tipo de comprador
- Notificaciones y alertas cuando aparece nueva demanda o cambian los precios en mercados cercanos
- Guía básica de calidad: qué calibre/calidad exigen los compradores, con ejemplos y consejos
- Cuadros de mando simples para cooperativas/ONG (seguimiento de oferta, demanda, coordinación)

## 3. Nivel de automatización recomendado
**Fase 1 — Asistencia a la decisión**: proporcionar a agricultores y compradores recomendaciones e información de precios/demanda, dejando las decisiones finales y negociaciones a las personas.

**Fase 2 — Notificación / acción semiautomática**: notificar automáticamente a los compradores y agregadores correspondientes las cosechas próximas y permitirles confirmar interés, con el agricultor aprobando el acuerdo final.

**Fase 3 — Automatización controlada**: permitir la reserva semiautomatizada de transporte y acuerdos de compra según reglas predefinidas, con supervisión humana y posibilidad de intervención.

## 4. Datos necesarios
**Datos internos (usuarios y socios):**
- Previsiones de cosecha: variedad, cajas estimadas, calibre/calidad, madurez, fecha de cosecha
- Ubicación de la finca: punto GPS, aldea, punto de agregación más cercano
- Fotos de los cultivos y de los tomates cosechados (evaluación de calidad/calibre)
- Notas de voz sobre el estado del cultivo, problemas o requisitos especiales
- Historial de ventas: fechas, volúmenes, precios, compradores, plazos de pago (si están disponibles)
- Perfiles de compradores: tipo (comerciante, agregador, mayorista), variedades preferidas, estándares de calidad, volúmenes típicos
- Datos de demanda: fechas de compra previstas, cantidades, precios objetivo, ubicaciones
- Datos logísticos: camiones disponibles, rutas, capacidades, costes típicos, horarios
- Registros de cooperativas/ONG: miembros, centros de agregación, coordinaciones pasadas

**Datos externos:**
- Precios de los mercados locales (informes de mercado del condado, datos de ONG)
- Información pública de extensión agraria (variedades, calibrado, postcosecha)
- Previsiones meteorológicas que afectan al calendario de cosecha y al estado de las carreteras
- Datos viales y de distancia (servicios de mapas) para estimar los costes de transporte
- Cobertura de red móvil para adaptar los canales (WhatsApp, SMS, USSD)

**Puntos de vigilancia de datos:**
- Calidad y fiabilidad de las previsiones de los agricultores y los compromisos de los compradores
- Estandarización de las definiciones de calibre/calidad entre actores
- Privacidad y consentimiento para compartir contactos/ubicaciones
- Acceso justo y prevención del mal uso de datos por intermediarios poderosos
- Cumplimiento de la normativa de protección de datos (incluido el RGPD si hay socios de la UE)

## 5. Orientaciones técnicas
**Componentes IA recomendados:**
- **LLM generalista** (GPT-4o, Claude Sonnet, Llama-3) para las interfaces conversacionales (bot de WhatsApp, chat móvil) y la explicación de precios, requisitos de calidad y opciones logísticas en lenguaje simple
- **ML clásico** (XGBoost o similar) para la predicción de rangos de precios y la previsión de demanda (historial, estacionalidad, ubicación)
- **Embeddings + búsqueda semántica** para casar las descripciones de cosecha con los perfiles de demanda y requisitos de calidad
- **Visión por computador** (CNN ligera o ViT + OCR móvil) para evaluar la calidad/el calibre en fotos y detectar problemas (sobremaduración, daños)
- **Clasificación NLP** para convertir notas de voz y texto libre en atributos estructurados («ligeramente demasiado maduros», «piel agrietada»)
- **Multiagente / orquestación** (LangGraph) para coordinar matching, estimación de precios, sugerencias logísticas y notificaciones en un workflow robusto

**Arquitectura objetivo:**
- Procesamiento híbrido: casi tiempo real para matching y notificaciones; batch para las actualizaciones diarias de precios/demanda y el reentrenamiento
- Estilo aplicativo: microservicios modulares o funciones serverless para controlar costes y escalar con el uso
- Integración: APIs para la app/bot de WhatsApp, webhooks para notificar a compradores y transportistas, pasarela USSD/SMS para teléfonos básicos
- Human-in-the-loop: agricultores y compradores confirman los acuerdos; respaldo de coordinación manual vía cooperativas/ONG; A/B testing de las estrategias de recomendación

**Integraciones clave:**
- API de WhatsApp Business y/o pasarela SMS/USSD
- App móvil o portal web simple para cooperativas/ONG y grandes compradores
- Servicios de mapas (Google Maps, OpenStreetMap) para distancias y rutas
- Servicios locales de información de precios o boletines de mercado existentes
- Soluciones de pago (dinero móvil tipo M-Pesa) para seguir estados y plazos de pago

**Build vs Buy:** Híbrido — apoyarse en las plataformas de mensajería y pago existentes y en los modelos IA del mercado, construyendo la capa de matching e inteligencia de mercado propia de la cadena del tomate de Kirinyaga.

**MLOps y gobernanza:**
- Seguimiento de la calidad de las recomendaciones (precisión de precios, tasa de matching exitoso, satisfacción)
- Reentrenamiento regular de los modelos de precios/demanda con las nuevas transacciones
- Registros de auditoría de las recomendaciones y acuerdos finales para la transparencia
- Gestión de sesgos y equidad (no desfavorecer a los pequeños productores; cumplimiento de protección de datos)
- Versionado de prompts, modelos y datasets

**Stack técnico previsto:**
- Cloud / runtime: cloud ligero (AWS, Azure, GCP) o proveedor regional con buena conectividad africana
- Modelos IA: GPT-4o o Claude Sonnet para la capa conversacional; Llama-3 open source para despliegues sensibles al coste; CNN/ViT pequeño para el calibrado por imagen; XGBoost para precios/demanda
- Capa de datos: base relacional (PostgreSQL) para las transacciones; almacenamiento de objetos para imágenes y notas de voz; base vectorial (Pinecone, Weaviate o pgvector) para el matching semántico
- Orquestación: LangGraph para los workflows multi-paso; Airflow o jobs serverless programados para el batch y el reentrenamiento
- Observabilidad: monitorización aplicativa (Prometheus + Grafana), logs (ELK), cuadros de mando dedicados al rendimiento de los modelos y al uso

## 6. Puntuación del caso de uso

| Criterio | Puntuación | Análisis |
| --- | --- | --- |
| Impacto de negocio | 9/10 | Reduce el desperdicio, mejora los precios, estabiliza los ingresos |
| Viabilidad de datos | 7/10 | Datos de agricultores/compradores disponibles pero por estructurar |
| Viabilidad técnica | 8/10 | Tecnologías IA y de mensajería maduras |
| Adopción | 8/10 | Gran necesidad; la familiaridad con WhatsApp facilita la adopción |
| Riesgo regulatorio | 6/10 | Moderado; sobre todo privacidad de datos y equidad |
| Potencial ROI | 9/10 | Grandes beneficios por menos pérdidas y mejores precios |

**Puntuación global estimada : 78/100**

**Veredicto:** Caso de uso sólido y de alto impacto, adecuado para un MVP focalizado y un escalado progresivo en la cadena del tomate.

## 7. Indicadores de éxito
**Indicadores de negocio:**
- Reducción de las pérdidas postcosecha (% menos de tomates no vendidos o estropeados)
- Aumento del precio medio en finca por caja (% de mejora)
- Cuota de volúmenes vendidos vía plataforma vs canales tradicionales
- Tiempo medio entre cosecha, confirmación de venta y pago
- Número de agricultores, compradores y transportistas activos
- Frecuencia de matchings exitosos (cosechas emparejadas con transacciones completadas)

**Indicadores IA:**
- Precisión de los rangos de precios predichos vs precios reales
- Tasa de matching exitoso (combinaciones comprador-logística que terminan en acuerdos cerrados)
- Calidad del calibrado por imagen (acuerdo con evaluadores humanos)
- Puntuaciones de satisfacción de agricultores/compradores (valoración simple tras la transacción)
- Métricas de deriva de los modelos (evolución de los patrones de precios/demanda)
- Latencia y fiabilidad de las recomendaciones y notificaciones

## 8. Conclusión
Los productores de tomate del condado de Kirinyaga afrontan un reto crítico: alinear cosechas altamente perecederas con la demanda en tiempo real, precios justos y una logística fiable. Un «Smart Tomato Market & Logistics Matcher» mobile-first apoyado en IA lo aborda directamente, convirtiendo entradas simples en recomendaciones accionables sobre compradores, precios, puntos de agregación y plazos de pago.

Empezando con un MVP ligero centrado en WhatsApp/SMS, un matching básico y orientación de precios, con cooperativas/ONG como coordinadoras, la solución puede demostrar valor rápidamente y generar confianza en la cadena. Con el tiempo podrán añadirse funciones más ricas (calibrado por imagen, previsión de demanda, reserva logística semiautomatizada).

**Recomendación:** Lanzar un MVP focalizado en Kirinyaga con un pequeño grupo de agricultores, compradores y una ONG/cooperativa, e iterar y escalar según los resultados reales.`;

const BY_LANG = { fr: FR, en: EN, es: ES };

export function getExampleDiagnostic(lang) {
  const key = (lang || 'fr').split('-')[0];
  return BY_LANG[key] || FR;
}

// Backwards-compatible default (historically French).
export const EXAMPLE_DIAGNOSTIC = FR;
